import { getDisplayTrackUrl } from '../TrackAnalyticsHelper';
import { logMessage } from '../sentry';
import { adStateEnum, AdUnitEventName } from '../../../@types/vpaid.type';

import { AdUnitStateManager } from './vpaid-ad-unit-state-manager';
import { AdUnitEventListener } from './vpaid-ad-unit-event-listener';

type VpaidEventMapType =
  | { track: string; condition: string[] }
  | ((...args: any[]) => void);

export class AdUnitTrackManager {
  _vpaidEvents: {
    [eventName: string]: VpaidEventMapType;
  } = {
    AdLoaded: {
      track: 'loaded',
      condition: [adStateEnum.IDLE]
    },
    AdStarted: {
      track: 'start',
      condition: [adStateEnum.LOADED]
    },
    AdStopped: {
      track: 'stop',
      condition: [adStateEnum.STARTED, adStateEnum.COMPLETED]
    },
    AdSkipped: {
      track: 'skipped',
      condition: [adStateEnum.STARTED, adStateEnum.COMPLETED]
    },
    AdLinearChange: {
      track: 'linearChange',
      condition: [adStateEnum.STARTED, adStateEnum.COMPLETED]
    },
    AdExpandedChange: {
      track: 'expandedChange',
      condition: [adStateEnum.STARTED]
    },
    AdSkippableStateChange: {
      track: 'skippableStateChange',
      condition: [adStateEnum.STARTED]
    },
    AdDurationChange: {
      track: 'durationChange',
      condition: [adStateEnum.STARTED]
    },
    AdVolumeChange: {
      track: 'volumeChange',
      condition: [adStateEnum.STARTED, adStateEnum.IDLE]
    },
    AdImpression: {
      track: 'impression',
      condition: [adStateEnum.STARTED]
    },
    AdClickThru: {
      track: 'clickThru',
      condition: [adStateEnum.STARTED, adStateEnum.COMPLETED]
    },
    AdInteraction: {
      track: 'interaction',
      condition: [adStateEnum.STARTED, adStateEnum.COMPLETED]
    },
    AdVideoStart: {
      track: 'videoStart',
      condition: [adStateEnum.STARTED]
    },
    AdVideoFirstQuartile: {
      track: 'firstQuartile',
      condition: [adStateEnum.STARTED]
    },
    AdVideoMidpoint: {
      track: 'midpoint',
      condition: [adStateEnum.STARTED]
    },
    AdVideoThirdQuartile: {
      track: 'thirdQuartile',
      condition: [adStateEnum.STARTED]
    },
    AdVideoComplete: {
      track: 'complete',
      condition: [adStateEnum.STARTED]
    },
    AdUserAcceptInvitation: {
      track: 'userAcceptInvitation',
      condition: [adStateEnum.STARTED, adStateEnum.COMPLETED]
    },
    AdUserMinimize: {
      track: 'userMinimize',
      condition: [adStateEnum.STARTED]
    },
    AdUserClose: {
      track: 'userClose',
      condition: [adStateEnum.STARTED, adStateEnum.COMPLETED]
    },
    AdPaused: {
      track: 'pause',
      condition: [adStateEnum.STARTED]
    },
    AdPlaying: {
      track: 'resume',
      condition: [adStateEnum.PAUSED]
    },
    AdSizeChange: {
      track: 'sizeChange',
      condition: [adStateEnum.STARTED, adStateEnum.PAUSED, adStateEnum.LOADED]
    },
    AdError: this._onAdError.bind(this)
  };

  _adEventPrefix = 'vpaid_';
  _stateManager: AdUnitStateManager;
  _eventListener: AdUnitEventListener;
  _ongoingTracks: {
    [url: string]: Promise<void>;
  } = {};

  constructor(
    stateManager: AdUnitStateManager,
    eventListener: AdUnitEventListener
  ) {
    this._stateManager = stateManager;
    this._eventListener = eventListener;
  }

  startTracking(): void {
    this._subscribeEvents();
  }

  waitForOngoingTracksToFinish(): Promise<void> {
    return Promise.all(
      Object.keys(this._ongoingTracks).map(k => this._ongoingTracks[k])
    ) as any;
  }

  _subscribeEvents(): void {
    for (const eventName in this._vpaidEvents) {
      const eventDescription = this._vpaidEvents[eventName];
      let eventHandler = () => {};

      if (typeof eventDescription === 'object') {
        eventHandler = () => {
          this._handleEvent(
            eventDescription as { track: string; condition: string[] }
          );
        };
      }

      if (typeof eventDescription === 'function') {
        eventHandler = eventDescription.bind(this);
      }

      this._eventListener.addEventListener(
        eventName as AdUnitEventName,
        eventHandler,
        -1
      );
    }
  }

  _onAdError(vast: any, error: any): void {
    logMessage('VPAID error', undefined, { error });
    this._trackEvent('error');
  }

  _handleEvent(eventDesc: { track: string; condition: string[] }): void {
    if (eventDesc.condition.indexOf(this._stateManager.getState()) === -1) {
      return;
    }

    this._trackEvent(eventDesc.track);
  }

  _trackEvent(event: string): void {
    const url = getDisplayTrackUrl(this._adEventPrefix + event);
    if (!url) {
      return;
    }

    const img = new Image();

    this._ongoingTracks[url] = new Promise(resolve => {
      img.onload = img.onerror = () => {
        delete this._ongoingTracks[url];
        resolve();
      };
    });

    img.src = url;
  }
}
